import Card from './ui/Card';

const features = [
  {
    icon: '📊',
    color: 'from-cyan-500 to-teal-500',
    title: 'Variable Income? No Problem.',
    description:
      'Set budgets for tight months, solid months, and months you can go all-in. See how each one changes your debt-free date.',
  },
  {
    icon: '🎯',
    color: 'from-teal-500 to-emerald-500',
    title: 'Exact Payment Schedules',
    description:
      'Know exactly what to pay each debt, every month. No spreadsheets. No confusion. Just a plan you can actually follow.',
  },
  {
    icon: '🧠',
    color: 'from-cyan-600 to-cyan-400',
    title: 'Smarter Than Snowball or Avalanche',
    description:
      "Most tools give you two options. Debt Chisel's AI analyzes your full portfolio — interest rates, balance sizes, cash flow, quick wins — and builds a payoff order optimized for your situation.",
  },
  {
    icon: '📈',
    color: 'from-emerald-500 to-green-500',
    title: 'Monthly Check-Ins That Keep You on Track',
    description:
      "Log what you actually paid each month — multiple payments, exact dates. See if you're ahead, behind, or on track — and what it means for your debt-free date. Real accountability, not just another dashboard.",
  },
  {
    icon: '🔒',
    color: 'from-teal-600 to-cyan-500',
    title: 'Your Data Stays Yours',
    description:
      "No bank logins. No third-party access to your accounts. You enter your balances, and that's all we ever see. We don't sell your data. Ever.",
  },
  {
    icon: '⚡',
    color: 'from-green-500 to-teal-500',
    title: 'Import Your Debts in Seconds',
    description:
      "Got a spreadsheet full of debts? Drop it in. Debt Chisel reads your CSV or Excel file, maps everything out, and gets you to your plan faster — no retyping required.",
  },
];

export default function Features() {
  return (
    <section id="features" className="px-6 pt-10 pb-24 bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4 text-fg">
            Built for Real Life
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Most debt tools assume steady income. Debt Chisel was built for the 70 million Americans
            whose income isn't the same every month.
          </p>
        </div>

        {/* Features Grid with colored accents */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="group hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center text-2xl shadow-md`}
                >
                  {feature.icon}
                </div>
                <h3 className="text-lg font-heading font-bold text-fg">{feature.title}</h3>
              </div>
              <p className="text-sm text-muted leading-relaxed">{feature.description}</p>
            </Card>
          ))}
        </div>

        {/* Comparison Callout with gradient */}
        <div className="mt-16 p-8 bg-gradient-to-br from-accent/5 via-bg to-secondary/5 border border-accent/20 rounded-2xl">
          <h3 className="text-2xl font-heading font-bold mb-6 text-center text-fg">
            Why Not Just Use a Spreadsheet?
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="p-4 bg-white/80 rounded-lg">
              <div className="text-4xl mb-3">📝</div>
              <p className="text-sm text-muted">
                Spreadsheets break with variable income and complex debt portfolios
              </p>
            </div>
            <div className="p-4 bg-white/80 rounded-lg">
              <div className="text-4xl mb-3">🤖</div>
              <p className="text-sm text-muted">
                No AI optimization — just manual math that's easy to get wrong
              </p>
            </div>
            <div className="p-4 bg-white/80 rounded-lg">
              <div className="text-4xl mb-3">📉</div>
              <p className="text-sm text-muted">
                No tracking, no revisions, no accountability month-to-month
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
